import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, TREATMENT_LABEL, type CatalogPreview, type Category, type Nature, type Treatment } from "../../api";
import { HistoryModal } from "../../components/HistoryModal";
import { Modal } from "../../components/Modal";
import { Btn, Field, Input, Notice, Select } from "../../components/ui";
import { useEntities } from "../../entity";
import { useMe } from "../../session";

const LEVEL = { 1: "Grupo", 2: "Categoria", 3: "Subcategoria" } as const;
type Form = { mode: "new"; nature: Nature; parent: Category | null } | { mode: "edit"; item: Category };

/** Catálogo de classificações do ambiente: Grupo → Categoria → Subcategoria. Compartilhado por todas as empresas do ambiente. */
export function Categories() {
  const { me } = useMe();
  const { entities } = useEntities();
  const realm = me.user.realm;
  const owner = entities[0]?.isOwner ?? false;
  const [items, setItems] = useState<Category[] | null>(null);
  const [nature, setNature] = useState<Nature>("despesa");
  const [form, setForm] = useState<Form | null>(null);
  const [hist, setHist] = useState<Category | null>(null);
  const [catalog, setCatalog] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  type Beh = "fixa" | "variavel";
  const [behMap, setBehMap] = useState<Map<string, { suggested: Beh; chosen: Beh | null; effective: Beh }>>(new Map());
  const load = useCallback(() => {
    if (realm === "pf") void api<{ items: { categoryId: string; suggested: Beh; chosen: Beh | null; effective: Beh }[] }>("GET", `/api/pf/pf-behavior-map`).then((r) => setBehMap(new Map(r.items.map((i) => [i.categoryId, i])))).catch(() => undefined);
    return api<Category[]>("GET", `/api/${realm}/categories`).then(setItems).catch((e) => setMsg({ kind: "error", text: errMsg(e) }));
  }, [realm]);
  useEffect(() => { void load(); }, [load]);

  const kids = useMemo(() => {
    const m = new Map<string | null, Category[]>();
    for (const c of items ?? []) {
      if (c.level === 1 && c.nature !== nature) continue;
      const k = c.parentId; m.set(k, [...(m.get(k) ?? []), c]);
    }
    for (const v of m.values()) v.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, "pt-BR"));
    return m;
  }, [items, nature]);

  async function act(fn: () => Promise<unknown>, ok: string) {
    try { await fn(); setMsg({ kind: "ok", text: ok }); setForm(null); await load(); }
    catch (e) { setMsg({ kind: "error", text: errMsg(e) }); await load(); }
  }
  const move = (c: Category, dir: -1 | 1) => {
    const sib = (kids.get(c.parentId) ?? []).map((x) => x.id);
    const i = sib.indexOf(c.id), j = i + dir;
    if (j < 0 || j >= sib.length) return;
    [sib[i], sib[j]] = [sib[j]!, sib[i]!];
    void act(() => api("PUT", `/api/${realm}/categories/order`, { parentId: c.parentId, nature: c.nature, ids: sib }), "Ordem atualizada.");
  };

  const node = (c: Category, siblings: Category[]): JSX.Element => {
    const idx = siblings.indexOf(c);
    return (
      <li key={c.id} data-cat={c.name} data-level={c.level} className={c.status === "inativa" ? "cat inactive" : "cat"}>
        <div className="cat-row" style={{ paddingLeft: (c.level - 1) * 22 }}>
          <span className="cat-name"><strong>{c.name}</strong> <span className="muted small">{LEVEL[c.level]}</span></span>
          {c.status === "inativa" && <span className="pill">Inativa</span>}
          {c.inUse && <span className="pill accent">Em uso</span>}
          {realm === "pf" && c.level === 2 && behMap.get(c.id) && <span className="pill" data-behavior={behMap.get(c.id)!.effective}>{behMap.get(c.id)!.effective === "fixa" ? "Fixa" : "Variável"}{behMap.get(c.id)!.chosen ? "" : " (auto)"}</span>}
          {c.level > 1 && c.treatment !== "resultado" && <span className="pill accent" data-treatment={c.treatment}>{TREATMENT_LABEL[c.treatment]}</span>}
          {c.systemKey && <span className="pill" data-system>Do sistema</span>}
          {owner && !c.systemKey && <span className="cat-actions">
            <button className="link" aria-label={`Subir ${c.name}`} disabled={idx === 0} onClick={() => move(c, -1)}>↑</button>
            <button className="link" aria-label={`Descer ${c.name}`} disabled={idx === siblings.length - 1} onClick={() => move(c, 1)}>↓</button>
            {c.status === "ativa" && c.level < 3 && <button className="link" onClick={() => { setMsg(null); setForm({ mode: "new", nature: c.nature, parent: c }); }}>+ {LEVEL[(c.level + 1) as 2 | 3]}</button>}
            {c.status === "ativa" && <button className="link" onClick={() => { setMsg(null); setForm({ mode: "edit", item: c }); }}>Editar</button>}
            {c.status === "ativa"
              ? <button className="link danger" onClick={() => void act(() => api("POST", `/api/${realm}/categories/${c.id}/inactivate`, { version: c.version }), `"${c.name}" inativada.`)}>Inativar</button>
              : <button className="link" onClick={() => void act(() => api("POST", `/api/${realm}/categories/${c.id}/reactivate`, { version: c.version }), `"${c.name}" reativada.`)}>Reativar</button>}
          </span>}
          <button className="link" onClick={() => setHist(c)}>Histórico</button>
        </div>
        {(kids.get(c.id) ?? []).length > 0 && <ul className="cat-tree">{(kids.get(c.id) ?? []).map((k, _i, arr) => node(k, arr))}</ul>}
      </li>
    );
  };
  const roots = kids.get(null) ?? [];

  return (
    <div className="card" data-categories>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <h3 style={{ fontSize: 16, marginBottom: 4 }}>Categorias</h3>
          <p className="muted" style={{ margin: 0 }}>Grupo → Categoria → Subcategoria.{realm === "pj" ? " Compartilhadas por todas as empresas do ambiente." : ""} Classificações já usadas nunca são apagadas: apenas renomeadas ou inativadas.</p>
        </div>
        <div className="seg" role="group" aria-label="Natureza">
          {(["despesa", "receita"] as const).map((n) => <button key={n} aria-pressed={nature === n} onClick={() => setNature(n)} data-nature={n}>{n === "despesa" ? "Despesas" : "Receitas"}</button>)}
        </div>
      </div>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {!owner && <p className="muted small">Somente o titular do ambiente altera as classificações.</p>}
      {items && items.length === 0 && owner && (
        <div className="notice" data-catalog-offer>
          <strong>Catálogo sugerido (opcional)</strong>
          <p className="small" style={{ margin: "4px 0 8px" }}>Comece com uma estrutura pronta de grupos e categorias para {realm === "pj" ? "empresas" : "pessoa física"}. Você vê tudo antes de aplicar e pode editar, inativar ou acrescentar depois.</p>
          <Btn variant="ghost" onClick={() => { setMsg(null); setCatalog(true); }} data-catalog-open>Usar catálogo sugerido</Btn>
        </div>
      )}
      {items && roots.length === 0 && <p className="muted" data-empty>Nenhum grupo de {nature === "despesa" ? "despesas" : "receitas"} cadastrado ainda.</p>}
      <ul className="cat-tree root">{roots.map((r, _i, arr) => node(r, arr))}</ul>
      {owner && <div style={{ marginTop: 12 }}><Btn variant="ghost" onClick={() => { setMsg(null); setForm({ mode: "new", nature, parent: null }); }}>+ Novo grupo</Btn></div>}
      {form && <CategoryModal form={form} items={items ?? []} onClose={() => setForm(null)}
        realm={realm} behaviorOf={(id) => behMap.get(id)}
        onSave={(name, parentId, treatment, behavior) => form.mode === "new"
          ? act(async () => { const n = await api<Category>("POST", `/api/${realm}/categories`, { name, parentId: form.parent?.id ?? null, nature: form.nature, ...(treatment ? { treatment } : {}) }, newIdemKey()); if (behavior) await api("PUT", `/api/${realm}/dre-map/${n.id}`, { group: null, behavior }); }, `"${name}" criada.`)
          : act(async () => { await api("PATCH", `/api/${realm}/categories/${form.item.id}`, { version: form.item.version, name, ...(parentId && parentId !== form.item.parentId ? { parentId } : {}), ...(treatment && treatment !== form.item.treatment ? { treatment } : {}) }); if (behavior !== undefined && behavior !== (behMap.get(form.item.id)?.chosen ?? null)) await api("PUT", `/api/${realm}/dre-map/${form.item.id}`, { group: null, behavior }); }, "Classificação atualizada.")} />}
      {catalog && <CatalogModal realm={realm} onClose={() => setCatalog(false)} onDone={(t) => { setCatalog(false); setMsg({ kind: "ok", text: t }); void load(); }} />}
      {hist && <HistoryModal title={`Histórico — ${hist.name}`} path={`/api/${realm}/categories/${hist.id}/history`} onClose={() => setHist(null)} />}
    </div>
  );
}

function CategoryModal({ form, items, realm, behaviorOf, onSave, onClose }: { form: Form; items: Category[]; realm: string; behaviorOf: (id: string) => { suggested: "fixa" | "variavel"; chosen: "fixa" | "variavel" | null } | undefined; onSave: (name: string, parentId: string | null, treatment: Treatment | null, behavior?: "fixa" | "variavel" | null) => Promise<void>; onClose: () => void }) {
  const edit = form.mode === "edit" ? form.item : null;
  const newParent = form.mode === "new" ? form.parent : null;
  const level = edit ? edit.level : newParent ? newParent.level + 1 : 1;
  const [name, setName] = useState(edit?.name ?? "");
  const [parentId, setParentId] = useState(edit?.parentId ?? "");
  const [busy, setBusy] = useState(false);
  const [treatment, setTreatment] = useState<Treatment>(edit?.treatment ?? "resultado");
  const nat = edit?.nature ?? (form.mode === "new" ? form.nature : "despesa");
  const showBeh = realm === "pf" && level === 2 && nat === "despesa" && treatment === "resultado";
  const cur = edit ? behaviorOf(edit.id) : undefined;
  const [beh, setBeh] = useState<"" | "fixa" | "variavel">(cur?.chosen ?? "");
  const parents = edit && edit.level > 1 ? items.filter((c) => c.level === edit.level - 1 && c.nature === edit.nature && c.status === "ativa") : [];
  async function submit(e: FormEvent) { e.preventDefault(); setBusy(true); try { await onSave(name.trim(), parentId || null, level === 2 ? treatment : null, showBeh ? (beh || null) : undefined); } finally { setBusy(false); } }
  return (
    <Modal title={edit ? `Editar ${LEVEL[level as 1 | 2 | 3].toLowerCase()}` : `Nova ${LEVEL[level as 1 | 2 | 3].toLowerCase()}`} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {newParent && <p className="muted small">Dentro de: {newParent.name}</p>}
        <Field label="Nome"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required name="categoryName" /></Field>
        {parents.length > 0 && (
          <Field label={`Pertence a (${LEVEL[(level - 1) as 1 | 2].toLowerCase()})`}>
            <Select value={parentId} onChange={(e) => setParentId(e.target.value)} name="categoryParent" disabled={edit?.inUse}>
              {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}
        {level === 2 && (
          <Field label="Tratamento financeiro">
            <Select value={treatment} onChange={(e) => setTreatment(e.target.value as Treatment)} name="categoryTreatment" disabled={edit?.inUse}>
              <option value="resultado">{TREATMENT_LABEL.resultado}</option>
              {(edit?.nature ?? (form.mode === "new" ? form.nature : "despesa")) === "despesa" && <option value="distribuicao_lucros">{TREATMENT_LABEL.distribuicao_lucros}</option>}
              {(edit?.nature ?? (form.mode === "new" ? form.nature : "despesa")) === "receita" && <option value="venda_ativo">{TREATMENT_LABEL.venda_ativo}</option>}
            </Select>
          </Field>
        )}
        {showBeh && (
          <Field label="Despesa fixa ou variável?">
            <Select value={beh} onChange={(e) => setBeh(e.target.value as "" | "fixa" | "variavel")} name="categoryBehavior">
              <option value="">{cur ? `Automática (pelo nome: ${cur.suggested === "fixa" ? "fixa" : "variável"})` : "Automática (pelo nome)"}</option>
              <option value="fixa">Fixa — valor parecido todo mês (aluguel, escola, internet)</option>
              <option value="variavel">Variável — muda conforme o uso (mercado, lazer, combustível)</option>
            </Select>
          </Field>)}
        {level === 2 && treatment !== "resultado" && <p className="muted small">{treatment === "distribuicao_lucros" ? "Distribuição de lucros sai do caixa, mas não entra como despesa operacional no resultado." : "Venda de ativos entra como caixa recebido, em linha própria — não é receita operacional."}</p>}
        {level === 3 && <p className="muted small">A subcategoria herda o tratamento da categoria.</p>}
        {edit?.inUse && <p className="muted small">Já utilizada em lançamentos: pode ser renomeada, mas não muda de lugar.</p>}
        <div className="row"><button type="submit" className="btn primary" disabled={busy || !name.trim()}>{busy ? "Salvando…" : "Salvar"}</button><Btn variant="ghost" onClick={onClose}>Cancelar</Btn></div>
      </form>
    </Modal>
  );
}

function CatalogModal({ realm, onClose, onDone }: { realm: string; onClose: () => void; onDone: (m: string) => void }) {
  const [prev, setPrev] = useState<CatalogPreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idem = useMemo(() => newIdemKey(), []);
  useEffect(() => { api<CatalogPreview>("GET", `/api/${realm}/catalog`).then(setPrev).catch((e) => setErr(errMsg(e))); }, [realm]);
  async function apply() {
    setBusy(true); setErr(null);
    try { await api("POST", `/api/${realm}/catalog/apply`, {}, idem); onDone(`Catálogo aplicado: ${prev?.items.length} grupos e ${prev?.categories} categorias. Tudo pode ser editado depois.`); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }
  return (
    <Modal title="Catálogo sugerido" onClose={onClose} wide>
      {err && <Notice kind="error">{err}</Notice>}
      {!prev && !err && <p className="muted">Carregando…</p>}
      {prev && (
        <>
          <p style={{ marginTop: 0 }}>{prev.items.length} grupos e {prev.categories} categorias, sem subcategorias. Nada é aplicado até você confirmar.</p>
          <div className="catalog-list" data-catalog-preview>
            {(["despesa", "receita"] as const).map((n) => (
              <div key={n}>
                <h4>{n === "despesa" ? "Despesas" : "Receitas"}</h4>
                <ul>{prev.items.filter((g) => g.nature === n).map((g) => (
                  <li key={g.name}><strong>{g.name}</strong>
                    <div className="small muted">{g.categories.map((c) => c.treatment === "resultado" ? c.name : `${c.name} (${TREATMENT_LABEL[c.treatment]})`).join(" · ")}</div></li>
                ))}</ul>
              </div>
            ))}
          </div>
          {!prev.canApply && <Notice>{prev.reason === "owner_only" ? "Somente o titular do ambiente aplica o catálogo." : "O catálogo só pode ser aplicado enquanto não houver nenhuma classificação."}</Notice>}
          <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn onClick={() => void apply()} disabled={!prev.canApply || busy} data-catalog-apply>{busy ? "Aplicando…" : "Aplicar catálogo"}</Btn></div>
        </>
      )}
    </Modal>
  );
}
